import { scoringVersion } from "./version.js";
/**
 * Volet 3 — barème du niveau de maîtrise (docs/SCORING.md §7).
 *
 * Huit axes, **deux questions par axe**, trois options notées 0, 1 ou 2.
 * Chaque axe vaut donc de 0 à 4, et le total va de 0 à 32.
 *
 * La seconde question de chaque axe est un **témoin** : générique, jamais
 * réécrite pour le métier, elle aborde le même axe par un autre angle. C'est
 * elle qui rend la mesure moins facile à flatter — avec une seule question par
 * axe, rien ne permettait de repérer une réponse de complaisance. Deux réponses
 * qui se contredisent sur un même axe se voient, se comptent, et sont dites.
 */
export const MASTERY_LEVELS = [
    {
        max: 10,
        niveau: 1,
        libelle: "Niveau 1 — Débutant",
        // « Spectateur » rabaissait, et « le niveau le plus fréquent » n'était
        // mesuré nulle part — règle n° 6. Relecture du 06/09/2026.
        texte: "Vous ne vous en servez pas encore. Ce n'est pas un jugement : c'est un point de départ, et c'est le niveau qui bouge le plus vite dès qu'on commence.",
    },
    {
        max: 18,
        niveau: 2,
        libelle: "Niveau 2 — Utilisateur occasionnel",
        texte: "Vous utilisez l'outil sans méthode. Les gains sont réels mais fragiles, et personne ne pourrait les reprendre à votre place.",
    },
    {
        max: 26,
        niveau: 3,
        libelle: "Niveau 3 — Praticien",
        texte: "Vous obtenez un résultat fiable et vous vérifiez ce que vous transmettez. Il vous manque la formalisation qui rendrait vos gains visibles.",
    },
    {
        max: 34,
        niveau: 4,
        libelle: "Niveau 4 — Référent",
        texte: "Vous êtes en avance sur votre marché. Votre enjeu n'est plus la compétence, mais sa reconnaissance — par votre entreprise ou par vos clients.",
    },
];
/** Total maximal : huit axes, deux questions chacun, notées 0 à 2. */
export const MASTERY_MAX_POINTS = 32;
/**
 * Nombre de questions réellement posées, et points maximum de chacune.
 *
 * **Pourquoi ces deux-là sont exportés.** Le volet a doublé le 12/08 — deux
 * questions par axe au lieu d'une — et trois endroits ont continué d'annoncer
 * huit : l'introduction du volet, et la portée écrite sur l'attestation, que
 * lit un employeur. Un nombre recopié à la main dérive ; un nombre dérivé du
 * barème ne le peut pas.
 *
 * Le serveur s'en sert aussi pour refuser un jeu de réponses qui n'a pas la
 * bonne forme : c'est la règle « le score est calculé par le code » appliquée à
 * l'entrée, et non seulement au calcul.
 */
export const MASTERY_QUESTIONS_PAR_AXE = 2;
export const POINTS_MAX_PAR_QUESTION = 2;
/**
 * Écart, sur un même axe, au-delà duquel les deux réponses se contredisent.
 *
 * Répondre 0 à une question et 2 à l'autre sur le même axe n'est pas une
 * nuance : c'est dire une chose et son contraire. Un ou deux écarts sur huit
 * axes restent ordinaires — les questions ne sont pas des jumelles. Au-delà, le
 * total mesure moins la pratique que le soin apporté à répondre, et le produit
 * doit le dire plutôt que de publier un niveau comme si de rien n'était.
 */
export const CONTRADICTION_ECART = 2;
/** Nombre d'axes contradictoires à partir duquel le résultat est signalé. */
export const CONTRADICTIONS_TOLEREES = 2;
export function levelForPoints(points) {
    const found = MASTERY_LEVELS.find((l) => points < l.max) ?? MASTERY_LEVELS[MASTERY_LEVELS.length - 1];
    return { niveau: found.niveau, libelle: found.libelle, texte: found.texte };
}
/**
 * Calcule le niveau de maîtrise à partir des réponses.
 *
 * Les réponses arrivent dans l'ordre d'affichage : axe 1 question 1, axe 1
 * question 2, axe 2 question 1… Le total est leur somme, comme avant ; ce qui
 * est nouveau, c'est qu'on regarde aussi **si les deux réponses d'un même axe
 * disent la même chose**.
 *
 * Les contradictions ne retirent aucun point. Retrancher reviendrait à punir
 * une personne pour une nuance que nos questions n'ont pas su capter, et à
 * mélanger deux choses distinctes : ce qui est mesuré, et la confiance qu'on
 * peut accorder à la mesure. On rend les deux séparément.
 *
 * @param reponses points obtenus par question (0, 1 ou 2), dans l'ordre affiché
 */
export function computeMastery(reponses) {
    const valide = (v) => (Number.isFinite(v) ? v : 0);
    const points = reponses.reduce((somme, v) => somme + valide(v), 0);
    let contradictions = 0;
    for (let i = 0; i + 1 < reponses.length; i += 2) {
        if (Math.abs(valide(reponses[i]) - valide(reponses[i + 1])) >= CONTRADICTION_ECART) {
            contradictions += 1;
        }
    }
    return {
        points,
        level: levelForPoints(points),
        contradictions,
        fiable: contradictions <= CONTRADICTIONS_TOLEREES,
        scoringVersion,
    };
}
export const MASTERY_AXES = [
    {
        axe: "delegation",
        mesure: "la façon de confier un travail écrit à une IA — du refus au cadrage explicite",
        q: "Vous devez écrire un courrier délicat à un client. Que faites-vous ?",
        o: [
            "Je l'écris entièrement moi-même, comme d'habitude",
            "Je demande un premier jet à une IA puis je le retravaille",
            "Je fournis le contexte, les contraintes et le ton attendu, puis j'itère",
        ],
        q2: "La dernière fois qu'une IA vous a rendu un texte, qu'en avez-vous fait ?",
        o2: [
            "Je l'ai laissé de côté et j'ai écrit le mien",
            "Je l'ai repris et corrigé",
            "Je le lui ai redemandé en précisant ce qui n'allait pas",
        ],
    },
    {
        axe: "verification",
        mesure: "le réflexe de vérifier un fait ou un chiffre produit par une IA",
        q: "Une IA vous donne un chiffre qui vous arrange. Votre réflexe ?",
        o: [
            "Je le reprends tel quel dans mon travail",
            "Je le vérifie quand il me surprend",
            "Je vérifie tout chiffre à la source avant de le transmettre",
        ],
        q2: "Un chiffre venu d'une IA part dans un document qui portera votre nom. Avant l'envoi :",
        o2: [
            "Je l'envoie, je l'ai lu",
            "Je le relis attentivement",
            "Je le retrouve dans la source d'origine",
        ],
    },
    {
        axe: "confidentialite",
        mesure: "le traitement des données sensibles de l'employeur ou des clients",
        q: "Documents confidentiels — les vôtres ou ceux de vos clients — et outils IA :",
        o: [
            "Je les copie dans l'outil quand j'en ai besoin",
            "Je m'abstiens dès qu'un document me paraît sensible",
            "J'ai une règle écrite et j'anonymise ce qui doit l'être",
        ],
        q2: "Le nom d'un client dans une demande adressée à une IA :",
        o2: [
            "Je l'écris tel quel",
            "Je le remplace quand j'y pense",
            "Je le remplace toujours par un repère neutre",
        ],
    },
    {
        axe: "frequence",
        mesure: "la fréquence réelle d'usage dans le travail",
        q: "Combien de fois par semaine utilisez-vous une IA dans votre travail ?",
        o: ["Rarement ou jamais", "Quelques fois", "Tous les jours, sur plusieurs tâches"],
        q2: "Depuis lundi dernier, combien de fois avez-vous ouvert une IA pour votre travail ?",
        o2: ["Aucune", "Une ou deux", "Presque chaque jour"],
    },
    {
        axe: "industrialisation",
        mesure: "le passage de l'usage ponctuel au mode opératoire réutilisable",
        q: "Une tâche répétitive vous prend 4 heures par semaine. Vous :",
        o: [
            "La faites comme vous l'avez toujours faite",
            "Vous aidez d'une IA quand vous y pensez",
            "Avez gardé une façon de faire écrite, que quelqu'un d'autre peut reprendre",
        ],
        q2: "Vos demandes à une IA, d'une fois sur l'autre :",
        o2: [
            "Je les réécris entièrement à chaque fois",
            "Je retrouve parfois une ancienne demande",
            "J'en garde par écrit et je les réutilise",
        ],
    },
    {
        axe: "attribution",
        mesure: "à quoi la personne impute un mauvais résultat — à l'outil ou à sa demande",
        q: "Vos résultats avec l'IA sont décevants. Vous en concluez :",
        o: [
            "Que l'outil ne convient pas à ce travail",
            "Qu'une prochaine version fera mieux",
            "Que ma demande manque de contexte, d'exemples ou de format attendu",
        ],
        q2: "Une réponse d'IA tombe à côté du sujet. Votre premier geste :",
        o2: [
            "Je passe à autre chose",
            "Je repose la question autrement",
            "J'ajoute ce qui manquait : le contexte, un exemple, le format attendu",
        ],
    },
    {
        axe: "diffusion",
        mesure: "la transmission de la compétence autour de soi",
        q: "Les gens de votre métier autour de vous, et l'IA :",
        o: [
            "Personne n'en parle vraiment",
            "On échange des astuces de temps en temps",
            "Je suis celui ou celle vers qui on vient pour apprendre",
        ],
        q2: "La dernière fois que vous avez trouvé une façon efficace d'utiliser une IA :",
        o2: [
            "Je l'ai gardée pour moi",
            "J'en ai parlé à quelqu'un du métier",
            "Je l'ai écrite quelque part où d'autres peuvent la reprendre",
        ],
    },
    {
        axe: "anticipation",
        mesure: "la capacité à nommer ce qui va changer dans son propre poste",
        q: "Sur votre métier dans deux ans :",
        o: [
            "Je ne me suis pas posé la question",
            "Certaines tâches vont changer, sans que je sache lesquelles",
            "J'ai identifié ce qui partira et ce sur quoi je me repositionne",
        ],
        q2: "Les parties de votre travail qu'une IA fera bientôt :",
        o2: [
            "Je n'en ai pas dressé la liste",
            "J'en vois quelques-unes, sans les avoir posées",
            "Je peux les nommer, et dire ce que je ferai à la place",
        ],
    },
];
